/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from '../../registry/Base.type';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionLoadData';

const ActionLoadDataManager: ActionManagerFn<'ActionLoadData'> = (
  platform: Platform,
): ActionManager<'ActionLoadData'> => {
  return {
    ...baseActionManager(platform, 'ActionLoadData'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['ActionSetSimpleQueryFilter'] as ActionItemType[]).includes(
        childType as ActionItemType,
      ),
    isAllowedInsideParent: (page, parent) => {
      return !['TriggerBeforeLoad'].includes(parent.type);
    },
    defaultChildren: ['ActionSetSimpleQueryFilter'],
    props: {
      ...baseActionProps,
      embeddedPageId: {
        label: 'Embedded Page ID',
        type: 'string',
      },
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        pageIdProp: 'embeddedPageId',
        allowedTypes: ['CloudDataStore'],
      },
      force: {
        label: 'Force',
        type: 'boolean',
      },
    },
    name: 'Load Data',
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'QUERY_FILTER'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    getDisplayText: (item) => item.props.alias,
    icon: { iconName: 'search' },
    description: `**Overview**<br/>
The Load Data action is a server-side data retrieval component designed to programmatically initiate a fetch operation from a Cloud Data Store. Unlike a local query which might just filter data already in memory, this action explicitly requests fresh data from the backend database.<br/><br/>
This component functions as a trigger for the store's load lifecycle. It allows developers to refresh data on demand (e.g., after a specific user interaction) rather than relying solely on the store's automatic loading settings. It strictly manages its usage context, preventing infinite recursion by forbidding its placement inside a TriggerBeforeLoad event.

**Key Features**<br/>
* **Cloud Store Specific:** Explicitly restricted (allowedTypes) to work only with CloudDataStore, ensuring it targets server-side data connections rather than local collections.
* **Recursion Protection:** Implements logic to block usage within TriggerBeforeLoad, preventing scenarios where a load action triggers a load event, which triggers the action again endlessly.
* **Ad-Hoc Filtering:** Supports ActionSetSimpleQueryFilter as a child component, allowing the developer to attach specific temporary constraints to this particular load operation.
* **Scope Targeting:** Includes an embeddedPageId property, enabling the action to trigger a data load on a store residing inside a nested or embedded page context.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the specific Cloud Data Store to refresh.
* **Force:** A boolean property.
  * *Function:* Forces a network request to the server even if the client believes the data is up-to-date or cached.
* **Embedded Page ID:** A string property used to resolve the store alias if it exists within an embedded page container.

**Use Cases**<br/>
* **Manual Refresh:** A "Reload" button on a dashboard that fetches the latest metrics from the server without reloading the entire browser page.
* **Dependent Data:** Triggering a load of the "Orders" store immediately after the "User" store has successfully finished loading (in a TriggerAfterLoad).`,
  };
};

export default ActionLoadDataManager;
